'use client';

import { toast } from 'sonner';
import { type FC, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';

import { siteConfig } from '@/config/site';
import MotionModal from '@/components/ui/motion-modal';
import { MarkdownReader } from '@/components/ui/markdown-reader';
import { Lightbox } from '@/components/ui/lightbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CustomSubtitle } from '@/components/custom/subtitle';

type RepairDocument = { url: string; type: string };
type RepairPhoto = {
  url: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
};

type RepairStatus = {
  id: string;
  name: string;
  description: string;
  createdAt: string;
};

type Note = {
  id: string;
  content: string;
  createdAt: string;
};

type RepairTrackingData = {
  statusCode: number;
  product: { name: string };
  brand: { name: string };
  notes: Note[];
  clientName: string;
  statuses: RepairStatus[];
  documents: RepairDocument[];
  photos: RepairPhoto[];
  reportActionUrl?: string;
};

enum Status {
  'success' = 'success',
  'error' = 'error',
  'loading' = 'loading',
  'idle' = 'idle',
  'closed' = 'closed',
}

type FetchState =
  | { status: Status.idle | Status.closed }
  | { status: Status.loading }
  | { status: Status.success; data: RepairTrackingData }
  | { status: Status.error; error: string };

type DocumentWithVT = Document & {
  startViewTransition?: (cb: () => void) => void;
};
let viewTransitionInFlight = false;

export const getDocumentLabel = (type: string) => {
  if (type === 'repairOrder') {
    return 'Orden de reparación';
  }

  if (type === 'report') {
    return 'Informe';
  }

  if (type === 'releaseOrder') {
    return 'Orden de salida';
  }

  if (type === 'warrantyOrder') {
    return 'Garantía';
  }

  if (type === 'internalQRs') {
    return 'QR Interno';
  }

  console.error(`Unknown document type :: ${type}`);

  return type;
};

const RepairTrackingModal: FC = () => {
  const router = useRouter();
  const pathname = usePathname();
  const sp = useSearchParams();

  const code = useMemo(() => sp.get('mt-code')?.trim() ?? '', [sp]);
  const open = !!code;

  const [state, setState] = useState<FetchState>({ status: Status.idle });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const hasError = state.status === Status.success && state.data.statusCode === 404;

  const abortRef = useRef<AbortController | null>(null);

  const replaceUrl = async (url: string) => {
    const doc = document as DocumentWithVT;

    const current = `${location.pathname}${location.search}`;

    if (url === current) return;

    if (!doc.startViewTransition || viewTransitionInFlight) {
      router.replace(url, { scroll: false });

      return;
    }

    viewTransitionInFlight = true;
    try {
      const vt = doc.startViewTransition(() => {
        router.replace(url, { scroll: false });
      });
      await vt.finished.catch(() => {});
    } finally {
      viewTransitionInFlight = false;
    }
  };

  const close = () => {
    const params = new URLSearchParams(sp);
    params.delete('mt-code');
    const url = params.toString() ? `${pathname}?${params}` : pathname;
    replaceUrl(url);
  };

  const openLightbox = (i: number) => {
    setStartIndex(i);
    setLightboxOpen(true);
  };

  const load = async (signal?: AbortSignal) => {
    try {
      setState({ status: Status.loading });
      const res = await fetch(`${siteConfig.apiUrl}/tracking/${encodeURIComponent(code)}`);
      const data: RepairTrackingData = await res.json();

      if (!signal?.aborted && res.ok) {
        setState({ status: Status.success, data });
      } else {
        setState({ status: Status.error, error: 'Algo salió mal.' });
      }
    } catch (err) {
      if (!signal || !signal.aborted) {
        setState({ status: Status.error, error: (err as Error).message });
      }
    }
  };

  const handleReportAction = async (type: 'accept' | 'reject') => {
    const accept = type === 'accept';

    try {
      if (state.status === Status.success && state.data.reportActionUrl) {
        setState({ status: Status.loading });

        const res = await fetch(state.data.reportActionUrl, {
          method: accept ? 'PATCH' : 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!res.ok) {
          throw new Error();
        } else {
          toast.success(accept ? 'Informe aceptado' : 'Informe rechazado');
          load();
        }
      }
    } catch {
      setState({ status: Status.error, error: 'Algo salió mal.' });
      toast.error('Ocurrió un error al actualizar el informe');
    }
  };

  useEffect(() => {
    if (!open) {
      setState({ status: Status.closed });
      abortRef.current?.abort();

      return;
    }

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    load(ac.signal);

    return () => ac.abort();
  }, [open, code]);

  return (
    <MotionModal
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          close();
        }
      }}
      animationStyle="from-center"
      viewTransitionName="repair-vt"
      contentClassName="max-w-6xl"
    >
      <div className="p-6 sm:p-8">
        <header className="mb-4 flex-col sm:flex-row flex gap-4 justify-between">
          <div className="flex flex-col text-center sm:text-left">
            <h3 className="text-lg font-medium mb-2">Seguimiento de reparación</h3>

            {state.status === Status.success && !hasError && (
              <Fragment>
                <p className="text-sm">
                  Producto:{' '}
                  <span className="text-muted-foreground">
                    {state.data.product.name} ({state.data.brand.name})
                  </span>
                </p>
                <p className="text-sm">
                  Cliente: <span className="text-muted-foreground">{state.data.clientName}</span>
                </p>
              </Fragment>
            )}

            <p className="text-sm">
              Código: <span className="font-mono text-muted-foreground">{code || '—'}</span>
            </p>
          </div>

          {state.status === Status.success && !!state.data.reportActionUrl && !hasError && (
            <div className="flex gap-2">
              <Button onClick={() => handleReportAction('reject')} variant="destructive">
                Rechazar
              </Button>
              <Button onClick={() => handleReportAction('accept')} className="bg-primary">
                Aceptar
              </Button>
            </div>
          )}
        </header>

        {state.status === 'loading' && (
          <div className="space-y-3">
            <div className="h-4 w-40 bg-muted animate-pulse rounded" />
            <div className="h-4 w-64 bg-muted animate-pulse rounded" />
            <div className="h-64 w-full bg-muted animate-pulse rounded" />
          </div>
        )}

        {state.status === Status.success && state.data.statusCode === 404 && (
          <div className="text-sm text-destructive">
            No encontramos ese código. Revisá que esté bien escrito.
          </div>
        )}

        {state.status === Status.error && (
          <div className="text-sm text-destructive">
            No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.
          </div>
        )}

        {state.status === Status.success && !hasError && (
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader className="flex flex-row items-start justify-between">
                  <CardTitle className="text-lg">Estados</CardTitle>
                </CardHeader>

                <CardContent className="flex flex-col">
                  {!!state.data.statuses.length && (
                    <div className="flex flex-col gap-8">
                      {state.data.statuses.map((status) => (
                        <div
                          key={status.name}
                          className="flex flex-col gap-2 border p-4 rounded-xl"
                        >
                          <div className="flex items-center gap-1">
                            <span className="text-md font-semibold">{status.name}</span>
                            <span className="text-sm text-muted-foreground">
                              (
                              {new Intl.DateTimeFormat('es-AR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }).format(new Date(status.createdAt))}
                              )
                            </span>
                          </div>
                          <span className="text-sm">{status.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>

                {state.data.notes.length > 0 && (
                  <Fragment>
                    <div className="border-b" />
                    <CardHeader className="flex flex-row items-start justify-between">
                      <CardTitle className="text-lg">Notas de la reparación</CardTitle>
                    </CardHeader>

                    <CardContent className="flex flex-col gap-4">
                      {state.data.notes.map((status, index) => (
                        <div key={status.id} className="flex flex-col gap-2 border p-4 rounded-xl">
                          <div className="flex flex-col justify-center items-start gap-1">
                            <span className="text-md font-semibold">
                              {`Nota #${index + 1} `}
                              <span className="text-sm text-muted-foreground">
                                (
                                {new Intl.DateTimeFormat('es-AR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                }).format(new Date(status.createdAt))}
                                )
                              </span>
                            </span>
                            <MarkdownReader content={status.content} />
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Fragment>
                )}
              </Card>
            </div>

            <div className="flex flex-col gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Documentos disponibles</CardTitle>
                  <CardDescription>
                    Accederás a más documentos conforme avance la reparación
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-4">
                    {state.data.documents.map((obj) => (
                      <div key={obj.url} className="py-2">
                        <a
                          className="flex items-center space-x-2 text-md font-medium text-blue-500 hover:text-blue-300"
                          href={obj.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <span>{getDocumentLabel(obj.type)}</span>
                        </a>
                      </div>
                    ))}

                    {!state.data.documents.length && (
                      <CustomSubtitle>Aún no hay documentos disponibles</CustomSubtitle>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg">Fotos disponibles</CardTitle>
                  <CardDescription>
                    Fotos de la reparación y del estado del dispositivo
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col">
                    {!state.data.photos.length && (
                      <CustomSubtitle>Aún no hay fotos cargadas</CustomSubtitle>
                    )}
                    {!!state.data.photos.length &&
                      state.data.photos.map((obj, index) => (
                        <div
                          key={obj.url}
                          className="flex cursor-pointer border-b border-muted py-2 last:border-b-0"
                          onClick={() => openLightbox(index)}
                        >
                          <div>
                            <Image
                              width={64}
                              height={42}
                              src={obj.url}
                              alt={obj.fileName}
                              className="h-14 w-16 rounded-md border-2 border-muted"
                            />
                          </div>
                          <div className="ml-2 flex flex-col justify-center">
                            <span className="text-sm font-medium ">{obj.fileName}</span>
                            <div className="space-x-2">
                              <span className="text-sm text-muted-foreground">{obj.mimeType}</span>
                              <span className="text-sm text-muted-foreground">
                                {obj.fileSize} KB
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {state.status === Status.success &&
          lightboxOpen &&
          !!state.data.photos.length &&
          !hasError && (
            <Lightbox
              images={state.data.photos.map((p) => p.url)}
              startIndex={startIndex}
              onClose={() => setLightboxOpen(false)}
            />
          )}
      </div>
    </MotionModal>
  );
};

export default RepairTrackingModal;
